<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  Bell,
  Promotion,
  ChatDotRound,
  Collection,
  DataAnalysis,
  Expand,
  Fold,
  Iphone,
  Menu as MenuIcon,
  SwitchButton,
  User,
  OfficeBuilding,
  Wallet,
} from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'
import { useAuthStore } from '../stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const collapsed = ref(false)

const navigation = [
  { path: '/meal-menus', label: '点餐菜单', icon: MenuIcon },
  { path: '/', label: '控制台', icon: DataAnalysis },
  { path: '/users', label: '用户管理', icon: User },
  { path: '/membership', label: '会员支付', icon: Wallet },
  { path: '/categories', label: '类别管理', icon: MenuIcon },
  { path: '/airlines', label: '航司管理', icon: OfficeBuilding },
  { path: '/advertisements', label: '广告管理', icon: Promotion },
  { path: '/question-banks', label: '默认题库', icon: Collection },
  { path: '/app-versions', label: '在线升级', icon: Iphone },
  { path: '/feedback', label: '意见反馈', icon: ChatDotRound },
]

const currentTitle = computed(() => route.meta.title ?? '管理后台')

async function logout() {
  await ElMessageBox.confirm('确定退出当前管理员账号吗？', '退出登录', {
    confirmButtonText: '退出',
    cancelButtonText: '取消',
    type: 'warning',
  })
  authStore.logout()
  await router.replace('/login')
}
</script>

<template>
  <div class="admin-layout" :class="{ collapsed }">
    <aside class="sidebar">
      <div class="brand">
        <span class="brand-mark"><Bell /></span>
        <span v-if="!collapsed" class="brand-copy">
          <strong>二号位</strong>
          <small>OPERATIONS</small>
        </span>
      </div>

      <nav class="navigation" aria-label="后台主导航">
        <RouterLink
          v-for="item in navigation"
          :key="item.path"
          :to="item.path"
          class="navigation-item"
          :title="collapsed ? item.label : undefined"
        >
          <component :is="item.icon" />
          <span v-if="!collapsed">{{ item.label }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer">
        <span v-if="!collapsed">V1 管理平台</span>
        <button
          class="collapse-button"
          type="button"
          :aria-label="collapsed ? '展开侧边栏' : '收起侧边栏'"
          @click="collapsed = !collapsed"
        >
          <Expand v-if="collapsed" />
          <Fold v-else />
        </button>
      </div>
    </aside>

    <div class="workspace">
      <header class="topbar">
        <div>
          <span class="eyebrow">二号位管理后台</span>
          <strong>{{ currentTitle }}</strong>
        </div>
        <div class="account">
          <span class="account-avatar">
            {{ authStore.administrator?.displayName?.slice(0, 1) ?? '管' }}
          </span>
          <div class="account-copy">
            <strong>{{ authStore.administrator?.displayName ?? '管理员' }}</strong>
            <small>{{ authStore.administrator?.username ?? 'admin' }}</small>
          </div>
          <button class="logout-button" type="button" title="退出登录" @click="logout">
            <SwitchButton />
          </button>
        </div>
      </header>

      <main class="content">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<style scoped>
.admin-layout {
  display: grid;
  grid-template-columns: var(--sidebar-width) 1fr;
  min-height: 100vh;
  transition: grid-template-columns 0.22s ease;
}

.admin-layout.collapsed {
  grid-template-columns: 84px 1fr;
}

.sidebar {
  position: sticky;
  z-index: 20;
  top: 0;
  display: flex;
  height: 100vh;
  min-width: 0;
  flex-direction: column;
  color: #fff;
  background:
    radial-gradient(circle at 30% 8%, rgb(89 111 218 / 55%), transparent 28%),
    linear-gradient(180deg, #202d68 0%, #17214e 100%);
}

.brand {
  display: flex;
  height: 84px;
  align-items: center;
  gap: 13px;
  padding: 0 22px;
  border-bottom: 1px solid rgb(255 255 255 / 10%);
  overflow: hidden;
}

.brand-mark {
  display: grid;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  place-items: center;
  border-radius: 13px;
  color: #263979;
  background: linear-gradient(145deg, #ffd07a, #ffab45);
  box-shadow: 0 8px 20px rgb(255 170 69 / 22%);
}

.brand-mark svg {
  width: 22px;
}

.brand-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.brand-copy strong {
  font-size: 17px;
  white-space: nowrap;
}

.brand-copy small {
  margin-top: 2px;
  color: rgb(255 255 255 / 50%);
  font-size: 9px;
  letter-spacing: 2px;
}

.navigation {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 7px;
  padding: 24px 14px;
}

.navigation-item {
  display: flex;
  height: 48px;
  align-items: center;
  gap: 13px;
  padding: 0 15px;
  border-radius: 12px;
  color: rgb(255 255 255 / 68%);
  font-size: 14px;
  transition:
    color 0.18s ease,
    background 0.18s ease;
  white-space: nowrap;
  overflow: hidden;
}

.navigation-item svg {
  width: 20px;
  flex: 0 0 20px;
}

.navigation-item:hover {
  color: #fff;
  background: rgb(255 255 255 / 7%);
}

.navigation-item.router-link-exact-active {
  color: #fff;
  background: linear-gradient(90deg, rgb(83 112 235 / 72%), rgb(100 119 213 / 35%));
  box-shadow: inset 3px 0 #ffc260;
}

.sidebar-footer {
  display: flex;
  height: 68px;
  align-items: center;
  justify-content: space-between;
  padding: 0 19px 0 24px;
  color: rgb(255 255 255 / 42%);
  font-size: 11px;
  white-space: nowrap;
}

.collapse-button,
.logout-button {
  display: grid;
  width: 34px;
  height: 34px;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 10px;
  color: inherit;
  background: transparent;
  cursor: pointer;
}

.collapse-button:hover {
  color: #fff;
  background: rgb(255 255 255 / 9%);
}

.collapse-button svg,
.logout-button svg {
  width: 18px;
}

.workspace {
  min-width: 0;
}

.topbar {
  position: sticky;
  z-index: 10;
  top: 0;
  display: flex;
  height: 84px;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  border-bottom: 1px solid var(--color-border);
  background: rgb(255 255 255 / 88%);
  backdrop-filter: blur(16px);
}

.topbar > div:first-child {
  display: flex;
  flex-direction: column;
}

.topbar strong {
  font-size: 16px;
}

.eyebrow {
  margin-bottom: 3px;
  color: var(--color-primary);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.7px;
}

.account {
  display: flex;
  align-items: center;
  gap: 10px;
}

.account-avatar {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(145deg, #4c6ae4, #263f9f);
  font-size: 14px;
  font-weight: 700;
}

.account-copy {
  display: flex;
  min-width: 86px;
  flex-direction: column;
}

.account-copy strong {
  font-size: 13px;
}

.account-copy small {
  color: var(--color-text-secondary);
  font-size: 11px;
}

.logout-button {
  color: var(--color-text-secondary);
}

.logout-button:hover {
  color: var(--color-danger);
  background: #fff0f1;
}

.content {
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 30px 32px 44px;
}

@media (max-width: 900px) {
  .admin-layout,
  .admin-layout.collapsed {
    grid-template-columns: 84px 1fr;
  }

  .brand-copy,
  .navigation-item span,
  .sidebar-footer > span {
    display: none;
  }

  .topbar {
    padding: 0 20px;
  }

  .content {
    padding: 24px 20px 36px;
  }
}

@media (max-width: 620px) {
  .admin-layout,
  .admin-layout.collapsed {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: fixed;
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    height: 68px;
  }

  .brand,
  .sidebar-footer {
    display: none;
  }

  .navigation {
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
    gap: 2px;
    padding: 8px;
  }

  .navigation-item {
    width: 44px;
    height: 44px;
    justify-content: center;
    padding: 0;
  }

  .navigation-item.router-link-exact-active {
    box-shadow: inset 0 -3px #ffc260;
  }

  .workspace {
    padding-bottom: 68px;
  }

  .account-copy {
    display: none;
  }
}
</style>
